import { useCallback } from 'react';

import { useAuthPagesRequestPasswordRecoveryMutation } from './request-password-recovery.v5.generated';

export type TRequestRecoveryMessage = {
  field: string;
  message: string;
  error_code: string;
};

export type TRequestRecoveryResult = {
  // ALWAYS true on a completed request (anti-enumeration: BE never reveals whether the
  // account exists). A falsy/missing response only happens on network/transport failure,
  // handled via `error` instead.
  success: boolean;
  messages: TRequestRecoveryMessage[];
};

export type TUseRequestRecoveryReturn = {
  requestRecovery: (
    email: string,
  ) => Promise<TRequestRecoveryResult | undefined>;
  data: TRequestRecoveryResult | undefined;
  loading: boolean;
  error: Error | undefined;
};

/**
 * Wires `security_policies.request_password_recovery` (v5, anonymous). `success` is
 * ALWAYS true on a completed response — the BE contract is anti-enumeration and never
 * reveals whether the account exists, so the view must render the confirmation state
 * unconditionally on success and only fall back to a generic error on network failure.
 * `next_url` is intentionally omitted from the input: it is a legacy field with no
 * effect on this v5 op.
 *
 * CORE-1795: `captcha_token` is intentionally NOT sent — BE removed it from
 * `RequestPasswordRecoveryInput` (verified via live introspection against
 * `qa.creha.co/api/v5/graph/`: the input now only exposes `value`/`next_url`).
 */
export const useRequestRecovery = (): TUseRequestRecoveryReturn => {
  const [mutate, { data, loading, error }] =
    useAuthPagesRequestPasswordRecoveryMutation({
      context: { clientName: 'v5' },
    });

  const requestRecovery = useCallback(
    async (email: string): Promise<TRequestRecoveryResult | undefined> => {
      // Mirrors use-confirm-reset: Apollo's mutate() rejects (not just resolves with
      // `.errors`) on a network failure under the default `errorPolicy: 'none'`. The
      // try/catch keeps that rejection from propagating as an unhandled promise
      // rejection out of the form's async onSubmit handler.
      let response;
      try {
        response = await mutate({
          variables: {
            input: { value: email },
          },
        });
      } catch {
        return undefined;
      }

      const payload =
        response.data?.security_policies.request_password_recovery;

      if (!payload) {
        return undefined;
      }

      return {
        success: payload.success,
        messages: payload.messages,
      };
    },
    [mutate],
  );

  const payload = data?.security_policies.request_password_recovery;
  const result: TRequestRecoveryResult | undefined = payload
    ? {
        success: payload.success,
        messages: payload.messages,
      }
    : undefined;

  return { requestRecovery, data: result, loading, error };
};
